import { FC, useMemo, type ReactNode } from 'react'
import classes from './index.module.css'
import { LeadField, LeadHistory } from '@/api/types/crm/crm.types'
import {
  buildFieldNamesById,
  collectFieldChangeLines,
  trimHistoryValue,
} from './historyFieldChanges'
import {
  formatCallDuration,
  formatDate,
  formatDateTime,
  formatTime,
} from '../../../../utils/formatData'

interface HistoryItemProps {
  historyItem: LeadHistory
  leadFields?: LeadField[]
}

const Em = ({ children }: { children: ReactNode }) => (
  <span className={classes.bold}>{children}</span>
)

const LEAD_IMPORT_STATE_LABELS: Record<string, string> = {
  new: 'Новый',
  in_work: 'В работе',
  processed: 'Обработан',
  client: 'Клиент',
}

const HistoryItem: FC<HistoryItemProps> = ({
  historyItem: item,
  leadFields,
}) => {
  const fieldNamesById = useMemo(
    () => buildFieldNamesById(leadFields),
    [leadFields],
  )
  let additionalInfo: Record<string, any> | null = null
  if (item?.additional_info) {
    try {
      additionalInfo = JSON.parse(item.additional_info)
      if (typeof additionalInfo !== 'object' || additionalInfo === null) {
        console.warn(
          'additional_info parsed to invalid object:',
          item.additional_info,
        )
        additionalInfo = null
      }
    } catch (error) {
      console.error('Failed to parse additional_info:', error)
      additionalInfo = null
    }
  }

  const getAdditionalInfo = (parameter: string) => {
    if (!additionalInfo || !(parameter in additionalInfo)) return null
    return additionalInfo[parameter]
  }

  /** Воронка, в контексте которой зафиксировано событие (для подписи у даты). */
  const funnelScopeName = (() => {
    const s = (v: unknown) =>
      v === null || v === undefined ? '' : String(v).trim()
    const fromRoot = s(item.funnel_name)
    if (fromRoot) return fromRoot
    if (!additionalInfo) return null
    const fromInfo = s(additionalInfo.funnel_name) || s(additionalInfo.funnel)
    return fromInfo || null
  })()

  /** Только `lead_import`: даты с бэка без пересчёта TZ, вид DD.MM.YY HH:mm. */
  const formatLeadImportDateTimeDisplay = (raw: string) => {
    const s = raw.trim()
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/,
    )
    if (!m) return s
    const [, y, mo, d, h, min] = m
    return `${d}.${mo}.${String(y).slice(-2)} ${h}:${min}`
  }

  const Content = () => {
    const changedByName =
      getAdditionalInfo('changed_by') ??
      (typeof item.changed_by === 'object' && item.changed_by
        ? item.changed_by.name
        : null) ??
      'неизвестно'

    const By = () => (
      <span className={classes.metaAuthor}>
        Изменил: {String(changedByName)}
      </span>
    )

    const renderFieldChangesBlock = (
      changes: unknown,
      mode: 'update' | 'create',
      head: string,
      emptyLabel: string,
    ) => {
      const lines = collectFieldChangeLines(changes, {
        mode,
        fieldNamesById,
        Em,
      })

      return (
        <>
          <div className={classes.leadChangesWrap}>
            <div className={classes.leadChangesHead}>{head}</div>
            {lines.length > 0 ? (
              <ul className={classes.leadChangesList}>
                {lines.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            ) : (
              <span className={classes.leadChangesEmpty}>{emptyLabel}</span>
            )}
          </div>
          <By />
        </>
      )
    }

    switch (item.change_type) {
      case 'message':
        return <Em>{item.change_description}</Em>

      case 'lead_create':
        return (
          <>
            Лид «<Em>{getAdditionalInfo('name')}</Em>» (
            <Em>{getAdditionalInfo('phone')}</Em>) был создан
            <By />
          </>
        )

      case 'lead_changes': {
        const changes = getAdditionalInfo('changes')
        if (!Array.isArray(changes)) {
          return (
            <span>
              {getAdditionalInfo('message') ?? 'Изменения'}: Некорректные данные
              <By />
            </span>
          )
        }

        return renderFieldChangesBlock(
          changes,
          'update',
          'Изменены поля',
          'без детализации',
        )
      }

      case 'fields_create': {
        const changes = getAdditionalInfo('changes')
        if (!Array.isArray(changes)) {
          return (
            <span>
              {getAdditionalInfo('message') ?? 'Новые поля'}: Некорректные данные
              <By />
            </span>
          )
        }

        const head = (() => {
          const msg = getAdditionalInfo('message')
          if (typeof msg === 'string' && msg.trim()) return msg.trim()
          return funnelScopeName ? 'Заполнены поля воронки' : 'Заполнены поля'
        })()

        return renderFieldChangesBlock(
          changes,
          'create',
          head,
          'без заполненных значений',
        )
      }

      case 'lead_import': {
        const importHead =
          typeof item.change_description === 'string' &&
          item.change_description.trim()
            ? item.change_description.trim()
            : 'Импортирован лид'

        const stateRaw = trimHistoryValue(getAdditionalInfo('state'))
        const stateLabel = stateRaw
          ? LEAD_IMPORT_STATE_LABELS[stateRaw] ?? stateRaw
          : ''

        const funnelLine =
          trimHistoryValue(getAdditionalInfo('funnel_name')) ||
          trimHistoryValue(item.funnel_name)

        const statusLine =
          trimHistoryValue(getAdditionalInfo('status_name')) ||
          trimHistoryValue(getAdditionalInfo('status'))

        const enteredAt = trimHistoryValue(getAdditionalInfo('entered_at'))
        const nextContactAt = trimHistoryValue(
          getAdditionalInfo('next_contact_at'),
        )

        const importRows: { key: string; label: string; value: string }[] = []
        const pushRow = (key: string, label: string, value: unknown) => {
          const v = trimHistoryValue(value)
          if (!v) return
          importRows.push({ key, label, value: v })
        }

        pushRow('name', 'Имя', getAdditionalInfo('name'))
        pushRow('phone', 'Телефон', getAdditionalInfo('phone'))
        if (funnelLine)
          importRows.push({ key: 'funnel', label: 'Воронка', value: funnelLine })
        if (statusLine)
          importRows.push({ key: 'status', label: 'Статус', value: statusLine })
        if (stateLabel)
          importRows.push({ key: 'state', label: 'Состояние', value: stateLabel })
        pushRow('manager', 'Менеджер', getAdditionalInfo('manager_name'))
        if (enteredAt)
          importRows.push({
            key: 'entered',
            label: 'Вход в воронку',
            value: formatLeadImportDateTimeDisplay(enteredAt),
          })
        if (nextContactAt)
          importRows.push({
            key: 'next',
            label: 'Следующий контакт',
            value: formatLeadImportDateTimeDisplay(nextContactAt),
          })

        return (
          <>
            <div className={classes.leadChangesWrap}>
              <div className={classes.leadChangesHead}>{importHead}</div>
              {importRows.length > 0 ? (
                <ul className={classes.leadChangesList}>
                  {importRows.map((r) => (
                    <li key={r.key}>
                      <Em>{r.label}</Em>: <Em>{r.value}</Em>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className={classes.leadChangesEmpty}>
                  Нет данных в additional_info
                </span>
              )}
            </div>
            <By />
          </>
        )
      }

      case 'lead_has_duplicate':
        return (
          <>
            Удален дубль «<Em>{getAdditionalInfo('name')}</Em>» (
            <Em>{getAdditionalInfo('phone')}</Em>) — дата следующего контакта
            обновлена.
            <By />
          </>
        )

      case 'add_status':
      case 'funnel_status_change': {
        const statusName =
          getAdditionalInfo('status_name') ??
          getAdditionalInfo('status') ??
          'неизвестно'

        const statusLine = (
          <span className={classes.statusLabelInline}>
            {String(statusName)}
            <span
              className={classes.itemStatusColor}
              style={{ backgroundColor: getAdditionalInfo('color') }}
              aria-hidden
            />
          </span>
        )

        if (funnelScopeName) {
          return (
            <>
              Изменение статуса на {statusLine}
              <By />
            </>
          )
        }

        const funnelName =
          item.funnel_name?.trim() ||
          (getAdditionalInfo('funnel_name') ??
            getAdditionalInfo('funnel') ??
            'неизвестно')

        return (
          <>
            Изменение статуса воронки «<Em>{String(funnelName)}</Em>» на статус{' '}
            {statusLine}
            <By />
          </>
        )
      }

      case 'delete_status':
        return (
          <>
            Удаление статуса{' '}
            <span className={classes.statusLabelInline}>
              {String(getAdditionalInfo('status') ?? '—')}
              <span
                className={classes.itemStatusColor}
                style={{ backgroundColor: getAdditionalInfo('color') }}
                aria-hidden
              />
            </span>
            <By />
          </>
        )

      case 'add_date_l': {
        const dateL = String(getAdditionalInfo('date'))
        return (
          <>
            Дата появления заявки:{' '}
            <Em>{dateL ? formatDateTime(dateL) : 'неизвестно'}</Em>
            <By />
          </>
        )
      }

      case 'add_date_z': {
        const dateZ = String(getAdditionalInfo('date'))
        return (
          <>
            Дата взятия заявки:{' '}
            <Em>{dateZ ? formatDateTime(dateZ) : 'неизвестно'}</Em>
            <By />
          </>
        )
      }

      case 'add_date_n': {
        const dateN = String(getAdditionalInfo('date'))
        return (
          <>
            Дата следующего контакта:{' '}
            <Em>{dateN ? formatDateTime(dateN) : 'неизвестно'}</Em>
            <By />
          </>
        )
      }

      case 'add_manager':
        return (
          <>
            Менеджер «<Em>{getAdditionalInfo('manager') ?? 'неизвестно'}</Em>» взял
            заявку.
            <By />
          </>
        )

      case 'delete_manager': {
        const removedManager =
          getAdditionalInfo('old_manager') ??
          getAdditionalInfo('manager') ??
          'неизвестно'
        return (
          <>
            Менеджер «<Em>{String(removedManager)}</Em>» снят с заявки
            <By />
          </>
        )
      }

      case 'sent_notification_by_button':
        return (
          <>
            Отправлено уведомление «<Em>{getAdditionalInfo('name')}</Em>» по кнопке
            <By />
          </>
        )

      case 'sent_notification_by_trigger':
        return (
          <>
            Отправлено уведомление «<Em>{getAdditionalInfo('name')}</Em>» по триггеру
            <By />
          </>
        )

      case 'funnel_add': {
        const funnelName =
          item.funnel_name?.trim() ||
          getAdditionalInfo('funnel_name') ||
          '—'
        return (
          <>
            Добавлена воронка «<Em>{funnelName}</Em>»
            <By />
          </>
        )
      }

      case 'funnel_remove': {
        const funnelName =
          item.funnel_name?.trim() ||
          getAdditionalInfo('funnel_name') ||
          '—'
        return (
          <>
            Удалена воронка «<Em>{funnelName}</Em>»
            <By />
          </>
        )
      }

      case 'call': {
        const duration = getAdditionalInfo('duration')
        return (
          <>
            Совершён звонок длительностью{' '}
            <Em>{duration ? formatCallDuration(duration) : 'неизвестно'}</Em>
            <By />
          </>
        )
      }

      case 'buy_course':
        return (
          <>
            Продан курс «<Em>{getAdditionalInfo('course_name') ?? 'неизвестно'}</Em>».
            <By />
          </>
        )

      case 'course_manual_add': {
        const courseType = getAdditionalInfo('course_type')
        const courseName = getAdditionalInfo('course_name')
        return (
          <>
            Добавлен{' '}
            {courseType ? (
              <Em>{String(courseType).toLowerCase()}</Em>
            ) : (
              'курс'
            )}{' '}
            «<Em>{courseName ?? 'неизвестно'}</Em>».
            <By />
          </>
        )
      }

      case 'course_purchase': {
        const courseName = getAdditionalInfo('course_name') ?? 'неизвестно'
        const linkCreatorName = getAdditionalInfo('link_creator_name')
        return (
          <>
            Куплен курс <Em>{String(courseName)}</Em>
            {linkCreatorName ? (
              <>
                {' '}
                (по ссылке: <Em>{String(linkCreatorName)}</Em>)
              </>
            ) : null}
            <By />
          </>
        )
      }

      case 'change_field': {
        const oldVal = getAdditionalInfo('old_value')
        const newVal = getAdditionalInfo('new_value')
        const isAutomation = getAdditionalInfo('is_automation')
        const fieldName = getAdditionalInfo('field') ?? 'неизвестно'

        return (
          <>
            Изменено поле «<Em>{String(fieldName)}</Em>»
            {isAutomation === 1 ? ' (автоматизация)' : ''}.
            {oldVal ? (
              <>
                {' '}
                Старое значение: «<Em>{String(oldVal)}</Em>».
              </>
            ) : null}
            {newVal ? (
              <>
                {' '}
                Новое значение: «<Em>{String(newVal)}</Em>».
              </>
            ) : null}
            <By />
          </>
        )
      }

      case 'change_state': {
        const oldState = getAdditionalInfo('old_state')
        const newState = getAdditionalInfo('new_state') ?? 'неизвестно'
        return (
          <>
            Заявка переведена{' '}
            {oldState ? (
              <>
                из состояния «<Em>{String(oldState)}</Em>»{' '}
              </>
            ) : null}
            в состояние «<Em>{String(newState)}</Em>».
            <By />
          </>
        )
      }

      case 'special_attr': {
        const attrName = String(getAdditionalInfo('attr_name') ?? '').toLowerCase()

        if (attrName === 'next_contact_at') {
          const oldRaw = getAdditionalInfo('old_value')
          const nextRaw = getAdditionalInfo('date')
          const oldValue = oldRaw ? formatDateTime(String(oldRaw)) : 'не указана'
          const nextValue = nextRaw ? formatDateTime(String(nextRaw)) : 'не указана'

          return (
            <>
              Изменена дата следующего контакта: <Em>{oldValue}</Em> →{' '}
              <Em>{nextValue}</Em>
              <By />
            </>
          )
        }

        return (
          <>
            <Em>{item.change_description ?? 'Изменен специальный атрибут'}</Em>
            <By />
          </>
        )
      }

      case 'lead_add_payment_data': {
        const paymentType = getAdditionalInfo('payment_type')
        const teacherName = getAdditionalInfo('teacher_name')
        const tariffName = getAdditionalInfo('tariff_name')
        const directionOrderName = getAdditionalInfo('direction_order_name')
        const groupName = getAdditionalInfo('group_name')
        const price = getAdditionalInfo('price')
        const classTitle = getAdditionalInfo('class_title')
        const paidFrom = getAdditionalInfo('paid_from')
        const paidTo = getAdditionalInfo('paid_to')

        return (
          <>
            Добавлен <Em>{String(paymentType ?? 'платёж')}</Em>
            {directionOrderName ? (
              <>
                : «<Em>{String(directionOrderName)}</Em>»
              </>
            ) : null}
            {classTitle ? (
              <>
                , «<Em>{String(classTitle)}</Em>»
              </>
            ) : null}
            {price ? (
              <>
                , за <Em>{String(price)}</Em> рублей
              </>
            ) : null}
            {paidFrom && paidTo ? (
              <>
                , с «<Em>{String(paidFrom)}</Em>» до «<Em>{String(paidTo)}</Em>»
              </>
            ) : null}
            {tariffName ? (
              <>
                . Тариф «<Em>{String(tariffName)}</Em>»
              </>
            ) : null}
            {groupName ? (
              <>
                . Группа «<Em>{String(groupName)}</Em>»
              </>
            ) : null}
            {teacherName ? (
              <>
                . Педагог <Em>{String(teacherName)}</Em>
              </>
            ) : null}
            .
            <By />
          </>
        )
      }

      default:
        return (
          <>
            <span>Неизвестное действие</span>
            <By />
          </>
        )
    }
  }

  return (
    <article className={classes.timelineItem}>
      <div className={classes.timelineRail} aria-hidden>
        <span className={classes.timelineDot} />
      </div>
      <div className={classes.timelineCard}>
        <div className={classes.cardTimeRow}>
          <div className={classes.timeMeta}>
            {formatDate(item.created_at)} · {formatTime(item.created_at)}
          </div>
          {funnelScopeName ? (
            <span
              className={classes.funnelScope}
              title={`По воронке «${funnelScopeName}»`}
            >
              по воронке «{funnelScopeName}»
            </span>
          ) : null}
        </div>
        <div className={classes.cardBody}>
          <Content />
        </div>
      </div>
    </article>
  )
}

export default HistoryItem
